import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Save, X, Users, Eye, Download, Bold, Italic, List, Underline } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsAdmin = () => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showApplicants, setShowApplicants] = useState(false);
  const [selectedJobApplications, setSelectedJobApplications] = useState(null);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    fullDescription: "",
    location: "",
    type: "",
    department: "",
    experience: "",
    salary: "",
    requirements: "",
    showOnWebsite: true,
    jobStatus: "Active"
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/admin/jobs`);
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const jobData = {
      ...formData,
      requirements: formData.requirements.split("\n").filter(req => req.trim() !== ""),
      salary: formData.salary.trim() || null  // Convert empty string to null
    };

    try {
      let response;
      if (editingJob) {
        response = await axios.put(`${API}/admin/jobs/${editingJob.id}`, jobData);
      } else {
        response = await axios.post(`${API}/admin/jobs`, jobData);
      }

      if (response.data.success) {
        await fetchJobs();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving job:", error);
      // Show error message if needed
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      }
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      description: job.description,
      fullDescription: job.fullDescription || "",
      location: job.location,
      type: job.type,
      department: job.department,
      experience: job.experience,
      salary: job.salary || "",  // Handle null salary
      requirements: Array.isArray(job.requirements) ? job.requirements.join("\n") : job.requirements,
      showOnWebsite: job.showOnWebsite !== undefined ? job.showOnWebsite : true,
      jobStatus: job.jobStatus || "Active"
    });
    setShowForm(true);
  };

  const handleDelete = async (jobId) => {
    if (window.confirm("Are you sure you want to delete this job posting?")) {
      try {
        const response = await axios.delete(`${API}/admin/jobs/${jobId}`);
        if (response.data.success) {
          await fetchJobs();
        }
      } catch (error) {
        console.error("Error deleting job:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      fullDescription: "",
      location: "",
      type: "",
      department: "",
      experience: "",
      salary: "",
      requirements: "",
      showOnWebsite: true,
      jobStatus: "Active"
    });
    setEditingJob(null);
    setShowForm(false);
  };

  const insertFormatting = (field, format) => {
    const textarea = document.querySelector(`textarea[name="${field}"]`);
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const val = textarea.value;
    const selected = val.substring(start, end);
    let newText = val;
    let newCursor = start;

    if (format === "bold") {
      const t = selected || "bold text";
      newText = val.substring(0, start) + `**${t}**` + val.substring(end);
      newCursor = start + 2 + t.length + 2;
    } else if (format === "italic") {
      const t = selected || "italic text";
      newText = val.substring(0, start) + `*${t}*` + val.substring(end);
      newCursor = start + 1 + t.length + 1;
    } else if (format === "underline") {
      const t = selected || "underlined text";
      newText = val.substring(0, start) + `__${t}__` + val.substring(end);
      newCursor = start + 2 + t.length + 2;
    } else if (format === "list") {
      const t = selected || "list item";
      newText = val.substring(0, start) + `\n- ${t}` + val.substring(end);
      newCursor = start + 3 + t.length;
    } else if (format.startsWith('size-')) {
      const px = format.split('-')[1];
      const t = selected || 'text';
      const open = `<span style="font-size:${px}px;color:inherit">`;
      newText = val.substring(0, start) + open + t + `</span>` + val.substring(end);
      newCursor = start + open.length + t.length + 7;
    } else {
      return;
    }

    setFormData(prev => ({ ...prev, [field]: newText }));
    setTimeout(() => { textarea.setSelectionRange(newCursor, newCursor); textarea.focus(); }, 0);
  };

  const handleRichTextKeyDown = (field, e) => {
    if (e.key !== 'Enter') return;
    const textarea = e.target;
    const pos = textarea.selectionStart;
    const val = textarea.value;
    const lineStart = val.lastIndexOf('\n', pos - 1) + 1;
    const currentLine = val.substring(lineStart, pos);
    if (!currentLine.startsWith('- ')) return;
    e.preventDefault();
    if (currentLine === '- ') {
      const newVal = val.substring(0, lineStart) + val.substring(pos);
      setFormData(prev => ({ ...prev, [field]: newVal }));
      setTimeout(() => textarea.setSelectionRange(lineStart, lineStart), 0);
    } else {
      const newVal = val.substring(0, pos) + '\n- ' + val.substring(pos);
      setFormData(prev => ({ ...prev, [field]: newVal }));
      setTimeout(() => { const np = pos + 3; textarea.setSelectionRange(np, np); }, 0);
    }
  };

  const handleViewApplicants = async (job) => {
    setApplicationsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/jobs/${job.id}/applications`);
      if (response.data.success) {
        setSelectedJobApplications(response.data.data);
        setShowApplicants(true);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleDownloadResume = (resumeUrl, applicantName) => {
    // Extract filename from the full path
    const filename = resumeUrl.split('/').pop();
    const downloadUrl = `${API}/admin/resumes/${filename}`;
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${applicantName.replace(/\s+/g, '_')}_Resume.${filename.split('.').pop()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeApplicantsModal = () => {
    setShowApplicants(false);
    setSelectedJobApplications(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-lg font-light text-gray-600">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 14, color: "#0A0A0A", marginBottom: 4 }}>
          Jobs
        </h1>
        <p style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#9CA3AF" }}>
          Manage job listings and review applications
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div />
        <button
          onClick={() => setShowForm(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            background: "#2D5C5A",
            border: "none",
            borderRadius: 6,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            fontSize: 13,
            color: "#fff",
            cursor: "pointer"
          }}
        >
          + Add Job
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto" style={{ color: "#374151" }}>
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-light">
                  {editingJob ? "Edit Job" : "Add New Job"}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                    placeholder="e.g. Senior AI Engineer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2">
                    Short Description *
                  </label>
                  <div className="border border-gray-200 rounded-sm">
                    <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                      <button type="button" onClick={() => insertFormatting("description", "bold")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                      <button type="button" onClick={() => insertFormatting("description", "italic")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                      <button type="button" onClick={() => insertFormatting("description", "underline")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                      <button type="button" onClick={() => insertFormatting("description", "list")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                      <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                      <select onMouseDown={(e) => e.stopPropagation()} onChange={(e) => { if (e.target.value) { insertFormatting("description", e.target.value); e.target.value = ''; } }} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                        <option value="">Normal</option>
                        <option value="size-10">10px</option>
                        <option value="size-11">11px</option>
                        <option value="size-12">12px</option>
                        <option value="size-13">13px (default)</option>
                        <option value="size-14">14px</option>
                        <option value="size-16">16px</option>
                        <option value="size-18">18px</option>
                        <option value="size-24">24px (large)</option>
                      </select>
                    </div>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleRichTextKeyDown("description", e)}
                      required
                      rows={3}
                      className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                      placeholder="Brief description of the role..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2">
                    Full Description
                  </label>
                  <div className="border border-gray-200 rounded-sm">
                    <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50" onMouseDown={(e) => e.preventDefault()}>
                      <button type="button" onClick={() => insertFormatting("fullDescription", "bold")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bold"><Bold size={15} /></button>
                      <button type="button" onClick={() => insertFormatting("fullDescription", "italic")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Italic"><Italic size={15} /></button>
                      <button type="button" onClick={() => insertFormatting("fullDescription", "underline")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Underline"><Underline size={15} /></button>
                      <button type="button" onClick={() => insertFormatting("fullDescription", "list")} className="p-1 text-gray-600 hover:text-black hover:bg-gray-200 rounded transition-colors duration-200" title="Bullet list"><List size={15} /></button>
                      <div style={{ width: 1, height: 14, background: '#e5e7eb', margin: '0 2px' }} />
                      <select onMouseDown={(e) => e.stopPropagation()} onChange={(e) => { if (e.target.value) { insertFormatting("fullDescription", e.target.value); e.target.value = ''; } }} className="text-xs text-gray-500 border border-gray-200 rounded px-1 bg-white focus:outline-none" style={{ height: 22 }}>
                        <option value="">Normal</option>
                        <option value="size-10">10px</option>
                        <option value="size-11">11px</option>
                        <option value="size-12">12px</option>
                        <option value="size-13">13px (default)</option>
                        <option value="size-14">14px</option>
                        <option value="size-16">16px</option>
                        <option value="size-18">18px</option>
                        <option value="size-24">24px (large)</option>
                      </select>
                    </div>
                    <textarea
                      name="fullDescription"
                      value={formData.fullDescription}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleRichTextKeyDown("fullDescription", e)}
                      rows={6}
                      className="w-full px-4 py-3 focus:outline-none font-light resize-none"
                      placeholder="Detailed job description..."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                      placeholder="e.g. San Francisco, CA / Remote"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Job Type *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-white"
                    >
                      <option value="">Select type</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Department *
                    </label>
                    <select
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-white"
                    >
                      <option value="">Select department</option>
                      <optgroup label="AI & Research">
                        <option value="AI Engineering">AI Engineering</option>
                        <option value="Machine Learning">Machine Learning</option>
                        <option value="AI Research">AI Research</option>
                      </optgroup>
                      <optgroup label="Development">
                        <option value="Frontend Engineering">Frontend Engineering</option>
                        <option value="Backend Engineering">Backend Engineering</option>
                        <option value="Full-Stack Engineering">Full-Stack Engineering</option>
                        <option value="Mobile Development">Mobile Development</option>
                        <option value="Cloud Engineering">Cloud Engineering</option>
                        <option value="DevOps">DevOps</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="Product">Product</option>
                        <option value="Design">Design</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Sales">Sales</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2">
                      Experience Level *
                    </label>
                    <input
                      type="text"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                      placeholder="e.g. 3+ years, Senior level, Entry level"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2">
                    Salary Range
                  </label>
                  <input
                    type="text"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light"
                    placeholder="e.g. $120k - $180k, $80 - $120/hour (optional)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to hide salary from job listings</p>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2">
                    Requirements *
                  </label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    required
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light resize-none"
                    placeholder="Enter each requirement on a new line:&#10;3+ years of AI/ML experience&#10;Strong Python skills&#10;Experience with TensorFlow or PyTorch&#10;..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter each requirement on a new line</p>
                </div>

                {/* New Job Visibility and Status Settings */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-light mb-4 text-gray-900">Job Visibility & Status</h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Website Visibility
                      </label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="showOnWebsite"
                            value="true"
                            checked={formData.showOnWebsite === true}
                            onChange={(e) => setFormData({...formData, showOnWebsite: e.target.value === 'true'})}
                            className="mr-2"
                          />
                          <span className="text-sm font-light">Show on careers page</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="showOnWebsite"
                            value="false"
                            checked={formData.showOnWebsite === false}
                            onChange={(e) => setFormData({...formData, showOnWebsite: e.target.value === 'true'})}
                            className="mr-2"
                          />
                          <span className="text-sm font-light">Hide from careers page</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Control whether this job appears on the public careers page</p>
                    </div>

                    <div>
                      <label className="block text-sm font-light text-gray-700 mb-2">
                        Job Status *
                      </label>
                      <select
                        name="jobStatus"
                        value={formData.jobStatus}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-black transition-colors duration-300 font-light bg-white"
                      >
                        <option value="Upcoming">Upcoming</option>
                        <option value="Active">Active</option>
                        <option value="Fulfilled">Fulfilled</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Only "Active" jobs appear on the careers page</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="bg-black text-white px-8 py-4 rounded-sm text-sm font-normal hover:bg-gray-900 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                  >
                    <Save size={16} />
                    {editingJob ? "Update Job" : "Create Job"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="border border-gray-300 text-gray-700 px-8 py-4 rounded-sm text-sm font-normal hover:border-black hover:text-black transition-all duration-300 hover:scale-105"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Jobs List */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB" }}>
              {["Title", "Dept", "Type", "Status", "Visible", "Actions"].map(col => (
                <th key={col} style={{ padding: "10px 16px", textAlign: "left", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 10, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{job.title}</td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{job.department}</td>
                <td style={{ padding: "12px 16px", fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 12, color: "#374151" }}>{job.type}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11,
                    fontFamily: "Montserrat, sans-serif", fontWeight: 500,
                    background: job.jobStatus === "Active" ? "#D1FAE5" : "#FEF3C7",
                    color: job.jobStatus === "Active" ? "#065F46" : "#92400E"
                  }}>
                    {job.jobStatus}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11,
                    fontFamily: "Montserrat, sans-serif", fontWeight: 500,
                    background: job.showOnWebsite ? "#D1FAE5" : "#FEF3C7",
                    color: job.showOnWebsite ? "#065F46" : "#92400E"
                  }}>
                    {job.showOnWebsite ? "Visible" : "Hidden"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleViewApplicants(job)}
                      style={{ padding: "5px 12px", background: "rgba(45,92,90,0.1)", border: "none", borderRadius: 4, fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 12, color: "#2D5C5A", cursor: "pointer" }}
                    >
                      Applications
                    </button>
                    <button
                      onClick={() => handleEdit(job)}
                      style={{ padding: "5px 12px", background: "rgba(45,92,90,0.1)", border: "none", borderRadius: 4, fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 12, color: "#2D5C5A", cursor: "pointer" }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(job.id)}
                      style={{ padding: "5px 12px", background: "#FEF2F2", border: "none", borderRadius: 4, fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: 12, color: "#DC2626", cursor: "pointer" }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Applicants Modal */}
      {showApplicants && selectedJobApplications && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-light">
                    Applicants for "{selectedJobApplications.job?.title}"
                  </h3>
                  <p className="text-gray-600 font-light">
                    {selectedJobApplications.totalApplications} applications received
                  </p>
                </div>
                <button
                  onClick={closeApplicantsModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  <X size={24} />
                </button>
              </div>

              {applicationsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-lg font-light text-gray-600">Loading applications...</div>
                </div>
              ) : selectedJobApplications.applications.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-4">👥</div>
                  <h4 className="text-xl font-light mb-4">No Applications Yet</h4>
                  <p className="text-gray-600 font-light">
                    This position hasn't received any applications yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {selectedJobApplications.applications.map((application) => (
                    <div key={application.id} className="bg-gray-50 p-6 rounded-sm border border-gray-200">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-light mb-2">{application.name}</h4>
                          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                            <div>
                              <strong>Email:</strong> {application.email}
                            </div>
                            {application.phone && (
                              <div>
                                <strong>Phone:</strong> {application.phone}
                              </div>
                            )}
                            <div>
                              <strong>Applied:</strong> {new Date(application.createdAt).toLocaleDateString()}
                            </div>
                            <div>
                              <strong>Status:</strong> {application.status}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          {application.resumeUrl && (
                            <button
                              onClick={() => handleDownloadResume(application.resumeUrl, application.name)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors duration-300"
                              title="Download Resume"
                            >
                              <Download size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {application.coverLetter && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h5 className="text-sm font-light text-gray-700 mb-2">Cover Letter:</h5>
                          <p className="text-sm text-gray-600 font-light leading-relaxed whitespace-pre-wrap">
                            {application.coverLetter}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsAdmin;